import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Flex,
  Grid,
  HStack,
  VStack,
  Text,
  Heading,
  Icon,
  Button,
  useDisclosure,
} from "@chakra-ui/react";
import {
  IoSparklesOutline,
  IoStorefrontOutline,
  IoArrowForwardOutline,
  IoVolumeMuteOutline,
  IoVolumeHighOutline,
  IoCashOutline,
  IoCalendarOutline,
  IoShieldCheckmarkOutline,
  IoPricetagOutline,
  IoPeopleOutline,
  IoFlashOutline,
  IoCheckmarkCircle,
} from "react-icons/io5";
import { useAuthState } from "react-firebase-hooks/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase/firebase-config";
import {
  SignInModal,
  VenueAgreementModal,
  VenuePayoutModal,
  VenueOnboardingModal,
  HowItWorksModal,
} from "../Components/Modals";
import {
  isVenueAgreementSigned,
  hasPayoutsReady,
  hasVerifiedStripeAccount,
} from "../lib/venueAgreement";
import venueFinalVideo from "../Assets/Videos/venueFinal.mp4";

// ─── Content ────────────────────────────────────────────────────────────────
// Swap out copy/icons here as the pitch evolves — everything else on the
// page is just layout plumbing around these arrays.
const BENEFITS = [
  {
    icon: IoCashOutline,
    accent: "#508cf7",
    accentSoft: "blue.50",
    title: "Zero cost to list",
    body: "Creating a venue profile is completely free. No listing fees, no monthly subscription, no fine print.",
  },
  {
    icon: IoShieldCheckmarkOutline,
    accent: "#22c55e",
    accentSoft: "green.50",
    title: "You approve every event",
    body: "Every booking request lands in your inbox first. Nothing goes live in your space without your yes.",
  },
  {
    icon: IoCalendarOutline,
    accent: "#a855f7",
    accentSoft: "purple.50",
    title: "Fill your dead nights",
    body: "Turn slow Tuesdays and empty off-seasons into a steady stream of new revenue from vetted event producers.",
  },
  {
    icon: IoPricetagOutline,
    accent: "#f59e0b",
    accentSoft: "orange.50",
    title: "You set the price",
    body: "Set your own minimum revenue per event and hourly rate. Proximy just handles the discovery and the money.",
  },
  {
    icon: IoFlashOutline,
    accent: "#ec4899",
    accentSoft: "pink.50",
    title: "Get paid directly, fast",
    body: "Payouts run straight to your bank through Stripe as tickets sell — no invoices, no chasing anyone down.",
  },
  {
    icon: IoPeopleOutline,
    accent: "#508cf7",
    accentSoft: "blue.50",
    title: "Real producers, not randoms",
    body: "Every producer on Proximy is vetted and agrees to a community agreement before they can book your space.",
  },
];

const STEPS = [
  {
    title: "Apply in minutes",
    body: "Tell us about your space — capacity, pricing, photos. Takes about five minutes, save-as-you-go.",
  },
  {
    title: "Get approved",
    body: "Our team reviews your listing and gets you live, usually within a couple of days.",
  },
  {
    title: "Start earning",
    body: "Producers request your space, you approve the ones you like, and payouts land automatically as tickets sell.",
  },
];

// ─── Vertical video player ────────────────────────────────────────────────
// Phone-frame mockup around the real venue reel, autoplaying like a social
// feed clip. Browsers only allow autoplay-without-a-click when the video
// starts muted — there's no way around that — so we start muted and give
// people a tap target to turn sound on.
const VenueVideoPreview = () => {
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    // Autoplay can still get blocked by aggressive browser settings —
    // fail silently and just show the first frame if so.
    videoEl.play().catch(() => {});
  }, []);

  const toggleMute = () => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    const nextMuted = !videoEl.muted;
    videoEl.muted = nextMuted;
    setIsMuted(nextMuted);
    if (!nextMuted) {
      // Unmuting counts as a fresh play request on some browsers.
      videoEl.play().catch(() => {});
    }
  };

  return (
    <Box position="relative" w="full" maxW={{ base: "300px", md: "340px" }} mx="auto">
      {/* Soft glow behind the phone frame */}
      <Box
        aria-hidden
        position="absolute"
        inset={0}
        bgGradient="linear(to-br, #508cf7, #a855f7)"
        opacity={0.28}
        filter="blur(50px)"
        borderRadius="40px"
        transform="translateY(20px)"
        zIndex={0}
      />

      <Box
        position="relative"
        zIndex={1}
        aspectRatio={9 / 16}
        w="full"
        borderRadius="14px"
        overflow="hidden"
        bg="gray.900"
        border="6px solid"
        borderColor="gray.900"
        boxShadow="0 24px 60px rgba(15,23,42,0.25), 0 8px 20px rgba(15,23,42,0.12)"
      >
        <Box
          as="video"
          ref={videoRef}
          src={venueFinalVideo}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onClick={toggleMute}
          w="full"
          h="full"
          objectFit="cover"
          cursor="pointer"
        />

        {/* Mute/unmute — the only control needed once it's autoplaying. */}
        <Box
          position="absolute"
          bottom={3}
          right={3}
          zIndex={2}
          onClick={toggleMute}
          cursor="pointer"
          bg="blackAlpha.600"
          borderRadius="full"
          p={2}
        >
          <Icon
            as={isMuted ? IoVolumeMuteOutline : IoVolumeHighOutline}
            boxSize="18px"
            color="white"
          />
        </Box>
      </Box>
    </Box>
  );
};

// Route back here after Stripe host onboarding — has to be a plain
// string constant (not derived from window.location) since it gets sent
// to the server as part of the Stripe account-link request.
const RETURN_PATH = "/list-your-venue";

export const VenueLanding = () => {
  const [user] = useAuthState(auth);
  const [searchParams, setSearchParams] = useSearchParams();

  const [pendingApply, setPendingApply] = useState(false);
  // null = not checked yet, so we never flash a step someone has already
  // completed while the Firestore read is in flight.
  const [agreementSigned, setAgreementSigned] = useState(null);
  const [payoutsReady, setPayoutsReady] = useState(null);
  // Already finished Stripe in another role (i.e. as a host) — they get a
  // "you're already connected" note instead of a second trip to Stripe.
  const [stripeConnected, setStripeConnected] = useState(false);

  const { isOpen: isSignInOpen, onOpen: onSignInOpen, onClose: onSignInClose } = useDisclosure();
  const { isOpen: isAgreementOpen, onOpen: onAgreementOpen, onClose: onAgreementClose } = useDisclosure();
  const { isOpen: isPayoutOpen, onOpen: onPayoutOpen, onClose: onPayoutClose } = useDisclosure();
  const { isOpen: isVenueModalOpen, onOpen: onVenueModalOpen, onClose: onVenueModalClose } = useDisclosure();
  const { isOpen: isHowItWorksOpen, onOpen: onHowItWorksOpen, onClose: onHowItWorksClose } = useDisclosure();

  // Listing a venue doesn't require being an event host — but it does
  // require signing the Venue Platform and Event Hosting Agreement and
  // having Stripe payouts set up, otherwise there's nowhere for the venue's
  // share of ticket revenue to land.
  useEffect(() => {
    if (!user) {
      setAgreementSigned(false);
      setPayoutsReady(false);
      setStripeConnected(false);
      return;
    }
    let cancelled = false;
    getDoc(doc(db, "users", user.uid))
      .then((snap) => {
        if (cancelled) return;
        const data = snap.exists() ? snap.data() : null;
        setAgreementSigned(isVenueAgreementSigned(data));
        setPayoutsReady(hasPayoutsReady(data));
        setStripeConnected(hasVerifiedStripeAccount(data));
      })
      .catch((err) => {
        console.error("Error fetching venue partner status:", err);
        if (!cancelled) {
          setAgreementSigned(false);
          setPayoutsReady(false);
          setStripeConnected(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Picks the next step in "List Your Venue": sign in → sign the venue
  // agreement → set up Stripe payouts → fill out the venue form.
  const advanceApplyFlow = () => {
    if (!agreementSigned) {
      onAgreementOpen();
      return;
    }
    if (!payoutsReady) {
      onPayoutOpen();
      return;
    }
    onVenueModalOpen();
  };

  // Once someone hits "List Your Venue", we may need to wait on a few async
  // things first: signing in, and the agreement/payout Firestore reads. This
  // fires the moment ALL are ready, whichever finishes last — covers
  // "signed out, still loading" and "signed in, still loading" in one place.
  useEffect(() => {
    if (pendingApply && user && agreementSigned !== null && payoutsReady !== null) {
      setPendingApply(false);
      advanceApplyFlow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingApply, user, agreementSigned, payoutsReady]);

  // Coming back from Stripe's hosted onboarding (see HostOnboarding.js) —
  // it appends `?openVenue=true` once the account is verified. Route it
  // through the same pending-apply flow so the agreement step still gets
  // enforced before the venue form opens.
  useEffect(() => {
    if (searchParams.get("openVenue") === "true" && user) {
      setPendingApply(true);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user]);

  const handleApplyClick = () => {
    if (!user) {
      setPendingApply(true);
      onSignInOpen();
      return;
    }
    if (agreementSigned === null || payoutsReady === null) {
      // Still loading — park the intent, the effect above picks it back up
      // as soon as everything resolves.
      setPendingApply(true);
      return;
    }
    advanceApplyFlow();
  };

  return (
    // `.App` sets a global `text-align: center` that every page inherits.
    // Reset it to `start` right here so every text node on this page
    // (headings, body copy, footer) left-aligns by default — only spots
    // that explicitly re-center (icon badges, numbered circles, buttons)
    // stay centered.
    <Box
      minH="calc(100vh - 62px)"
      width="100%"
      overflow="hidden"
      position="relative"
      bg="white"
      textAlign="start"
    >
      {/* Decorative blurred orbs, desktop only — matches the main landing page. */}
      <Box
        aria-hidden
        display={{ base: "none", md: "block" }}
        position="absolute"
        top="-160px"
        left="-120px"
        w="420px"
        h="420px"
        bg="#508cf7"
        opacity={0.16}
        filter="blur(120px)"
        borderRadius="full"
        pointerEvents="none"
        zIndex={0}
      />
      <Box
        aria-hidden
        display={{ base: "none", md: "block" }}
        position="absolute"
        top="480px"
        right="-140px"
        w="420px"
        h="420px"
        bg="#a855f7"
        opacity={0.1}
        filter="blur(140px)"
        borderRadius="full"
        pointerEvents="none"
        zIndex={0}
      />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <Container maxW="container.xl" px={{ base: "20px", md: "30px", lg: "60px" }} position="relative" zIndex={1}>
        <Flex
          alignItems="center"
          w="full"
          justifyContent="space-between"
          py={{ base: "48px", md: "72px" }}
          gap={{ base: "40px", md: "60px", lg: "80px" }}
          direction={{ base: "column", lg: "row" }}
        >
          {/* Copy + CTA */}
          <Flex textAlign="start" flexDirection="column" flex="1" maxW={{ base: "100%", lg: "54%" }} w="full">
            <HStack
              spacing={2}
              bg="blue.50"
              color="#226cf1"
              borderRadius="full"
              px={3}
              py={1.5}
              alignSelf="flex-start"
              mb={5}
              border="1px solid"
              borderColor="blue.100"
            >
              <Icon as={IoStorefrontOutline} boxSize="14px" />
              <Text fontSize="12px" fontWeight="700" letterSpacing="0.02em">
                For Venue Owners
              </Text>
            </HStack>

            <Heading
              className="font-gilroy-bold"
              fontSize={{ base: "38px", md: "52px", lg: "60px" }}
              lineHeight={{ base: "46px", md: "58px", lg: "66px" }}
              letterSpacing="-0.02em"
              color="gray.800"
            >
              Turn your space into{" "}
              <Box as="span" color="#508cf7">
                the venue
              </Box>{" "}
              everyone's talking about.
            </Heading>

            <Text
              fontWeight="500"
              pt={5}
              fontSize={{ base: "15px", md: "17px" }}
              color="gray.500"
              lineHeight="1.65"
              maxW="540px"
            >
              List your space for free, get discovered by vetted event
              producers, and fill your slow nights with events you actually
              approve — while Proximy handles ticketing and payouts.
            </Text>

            <HStack spacing={3} mt={8} flexWrap="wrap">
              <Button
                onClick={handleApplyClick}
                size="lg"
                bgGradient="linear(to-r, #508cf7, #226cf1)"
                color="white"
                fontSize="15px"
                fontWeight="700"
                borderRadius="14px"
                px={7}
                h="54px"
                rightIcon={<Icon as={IoArrowForwardOutline} boxSize="18px" />}
                boxShadow="0 10px 24px rgba(80,140,247,0.35)"
                _hover={{
                  bgGradient: "linear(to-r, #226cf1, #1d4ed8)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 14px 32px rgba(80,140,247,0.45)",
                }}
                _active={{ transform: "translateY(0)" }}
                style={{ transition: "transform 0.2s ease, box-shadow 0.2s ease" }}
              >
                List Your Venue
              </Button>

              <Button
                onClick={onHowItWorksOpen}
                variant="ghost"
                size="lg"
                fontSize="14px"
                fontWeight="600"
                color="gray.700"
                borderRadius="14px"
                h="54px"
                px={5}
                _hover={{ bg: "gray.50", color: "#226cf1" }}
              >
                See how it works
              </Button>
            </HStack>

            <HStack spacing={1.5} mt={3} color="gray.400">
              <Icon as={IoShieldCheckmarkOutline} boxSize="12px" flexShrink={0} />
              <Text fontSize="11px" fontWeight="500">
                Quick Stripe payout setup is part of listing — so you get paid
                the moment tickets sell.
              </Text>
            </HStack>

            <HStack spacing={2} mt={5} flexWrap="wrap">
              {[
                { icon: IoFlashOutline, label: "Free to list" },
                { icon: IoShieldCheckmarkOutline, label: "You approve every event" },
                { icon: IoCashOutline, label: "Fast direct payouts" },
              ].map(({ icon, label }) => (
                <HStack
                  key={label}
                  spacing={1.5}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.150"
                  borderRadius="full"
                  px={3}
                  py={1.5}
                  boxShadow="0 1px 3px rgba(0,0,0,0.04)"
                >
                  <Icon as={icon} boxSize="14px" color="#508cf7" />
                  <Text fontSize="12px" fontWeight="600" color="gray.600">
                    {label}
                  </Text>
                </HStack>
              ))}
            </HStack>
          </Flex>

          {/* Vertical video placeholder */}
          <Flex flex="1" justifyContent="center" alignItems="center" maxW={{ base: "100%", lg: "42%" }} w="full">
            <VenueVideoPreview />
          </Flex>
        </Flex>
      </Container>

      {/* ── Benefits grid ────────────────────────────────────────────────── */}
      <Box position="relative" zIndex={1} bg="gray.50" borderTop="1px solid" borderColor="gray.100">
        <Container maxW="container.xl" px={{ base: "20px", md: "30px", lg: "60px" }} py={{ base: "56px", md: "80px" }}>
          <VStack spacing={2} mb={{ base: 8, md: 12 }} maxW="640px">
            <Text
              alignSelf="flex-start"
              fontSize="11px"
              fontWeight="800"
              letterSpacing="0.1em"
              textTransform="uppercase"
              color="#508cf7"
            >
              Why venues partner with us
            </Text>
            <Heading
              alignSelf="flex-start"
              className="font-gilroy-bold"
              fontSize={{ base: "26px", md: "34px" }}
              lineHeight={{ base: "32px", md: "40px" }}
              letterSpacing="-0.01em"
              color="gray.800"
            >
              Everything to say yes, nothing to lose.
            </Heading>
          </VStack>

          <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={{ base: 4, md: 5 }}>
            {BENEFITS.map((b) => (
              <Box
                key={b.title}
                bg="white"
                borderRadius="2xl"
                border="1px solid"
                borderColor="gray.100"
                p={{ base: 5, md: 6 }}
                boxShadow="0 1px 4px rgba(0,0,0,0.04)"
                _hover={{
                  borderColor: "blue.100",
                  boxShadow: "0 8px 24px rgba(80,140,247,0.10)",
                  transform: "translateY(-3px)",
                }}
                style={{ transition: "all 0.2s ease" }}
              >
                <Flex
                  w="44px"
                  h="44px"
                  borderRadius="xl"
                  bg={b.accentSoft}
                  align="center"
                  justify="center"
                  mb={4}
                >
                  <Icon as={b.icon} boxSize="20px" color={b.accent} />
                </Flex>
                <Text fontWeight="700" fontSize="md" color="gray.800" mb={1.5}>
                  {b.title}
                </Text>
                <Text fontSize="13px" color="gray.500" lineHeight="1.65">
                  {b.body}
                </Text>
              </Box>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── How it works ─────────────────────────────────────────────────── */}
      <Box position="relative" zIndex={1} bg="white">
        <Container maxW="container.xl" px={{ base: "20px", md: "30px", lg: "60px" }} py={{ base: "56px", md: "80px" }}>
          <VStack spacing={2} mb={{ base: 8, md: 12 }} maxW="640px">
            <Text
              alignSelf="flex-start"
              fontSize="11px"
              fontWeight="800"
              letterSpacing="0.1em"
              textTransform="uppercase"
              color="#508cf7"
            >
              How it works
            </Text>
            <Heading
              alignSelf="flex-start"
              className="font-gilroy-bold"
              fontSize={{ base: "26px", md: "34px" }}
              lineHeight={{ base: "32px", md: "40px" }}
              letterSpacing="-0.01em"
              color="gray.800"
            >
              Three steps from empty room to sold out.
            </Heading>
          </VStack>

          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={{ base: 5, md: 6 }}>
            {STEPS.map((step, i) => (
              <Box key={step.title} position="relative">
                <Flex
                  w="40px"
                  h="40px"
                  borderRadius="full"
                  bg="blue.50"
                  border="1px solid"
                  borderColor="blue.100"
                  color="#508cf7"
                  align="center"
                  justify="center"
                  fontSize="15px"
                  fontWeight="800"
                  mb={4}
                >
                  {i + 1}
                </Flex>
                <Text fontWeight="700" fontSize="lg" color="gray.800" mb={2}>
                  {step.title}
                </Text>
                <Text fontSize="14px" color="gray.500" lineHeight="1.7">
                  {step.body}
                </Text>
              </Box>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Final CTA ────────────────────────────────────────────────────── */}
      <Box position="relative" zIndex={1} bg="gray.50" borderTop="1px solid" borderColor="gray.100">
        <Container maxW="container.xl" px={{ base: "20px", md: "30px", lg: "60px" }} py={{ base: "56px", md: "72px" }}>
          <Box
            position="relative"
            borderRadius="28px"
            overflow="hidden"
            bgGradient="linear(135deg, #0f172a 0%, #1e293b 60%, #0f172a 100%)"
            px={{ base: 6, md: 12 }}
            py={{ base: 10, md: 14 }}
            boxShadow="0 20px 50px rgba(15,23,42,0.25)"
          >
            <Box
              aria-hidden
              position="absolute"
              top="-80px"
              right="-80px"
              w="260px"
              h="260px"
              bg="#508cf7"
              opacity={0.25}
              filter="blur(80px)"
              borderRadius="full"
              pointerEvents="none"
            />
            <Box
              aria-hidden
              position="absolute"
              bottom="-90px"
              left="-90px"
              w="240px"
              h="240px"
              bg="#22c55e"
              opacity={0.16}
              filter="blur(80px)"
              borderRadius="full"
              pointerEvents="none"
            />

            <Flex
              position="relative"
              zIndex={1}
              direction={{ base: "column", md: "row" }}
              align={{ base: "flex-start", md: "center" }}
              justify="space-between"
              gap={6}
            >
              <VStack align="flex-start" spacing={3} maxW="560px">
                <HStack
                  spacing={1.5}
                  bg="whiteAlpha.100"
                  border="1px solid"
                  borderColor="whiteAlpha.200"
                  borderRadius="full"
                  px={3}
                  py={1}
                >
                  <Icon as={IoSparklesOutline} boxSize="13px" color="#22c55e" />
                  <Text fontSize="11px" fontWeight="700" color="whiteAlpha.800" letterSpacing="0.04em">
                    Free to join · No commitment
                  </Text>
                </HStack>
                <Heading
                  className="font-gilroy-bold"
                  fontSize={{ base: "24px", md: "30px" }}
                  lineHeight={{ base: "30px", md: "36px" }}
                  letterSpacing="-0.01em"
                  color="white"
                >
                  Your space is one application away from its next sold-out night.
                </Heading>
              </VStack>

              <Button
                onClick={handleApplyClick}
                size="lg"
                bg="white"
                color="#0f172a"
                fontSize="15px"
                fontWeight="700"
                borderRadius="14px"
                px={7}
                h="54px"
                flexShrink={0}
                rightIcon={<Icon as={IoArrowForwardOutline} boxSize="18px" />}
                _hover={{ bg: "gray.100", transform: "translateY(-2px)" }}
                _active={{ transform: "translateY(0)" }}
                style={{ transition: "transform 0.2s ease" }}
              >
                List Your Venue
              </Button>
            </Flex>

            <HStack spacing={5} mt={7} flexWrap="wrap" position="relative" zIndex={1}>
              {["No listing fees", "You approve every booking", "Cancel anytime"].map((label) => (
                <HStack key={label} spacing={1.5}>
                  <Icon as={IoCheckmarkCircle} boxSize="14px" color="#22c55e" />
                  <Text fontSize="12px" color="whiteAlpha.800" fontWeight="500">
                    {label}
                  </Text>
                </HStack>
              ))}
            </HStack>
          </Box>
        </Container>
      </Box>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <Box position="relative" zIndex={1} borderTop="1px solid" borderColor="gray.100" bg="white">
        <Container maxW="container.xl" px={{ base: "20px", md: "30px", lg: "60px" }} py={4}>
          <Flex direction={{ base: "column", sm: "row" }} align={{ base: "flex-start", sm: "center" }} justify="space-between" gap={2}>
            <Text fontSize="11px" color="gray.400">
              &copy; {new Date().getFullYear()} Proximy Technologies, Inc.
            </Text>
            <HStack spacing={4} color="gray.400" fontSize="11px">
              <Box as="a" href="https://proximy-site.web.app/terms" target="_blank" rel="noopener noreferrer" _hover={{ color: "#508cf7", textDecoration: "underline" }}>
                Terms
              </Box>
              <Box as="a" href="https://proximy-site.web.app/privacy" target="_blank" rel="noopener noreferrer" _hover={{ color: "#508cf7", textDecoration: "underline" }}>
                Privacy
              </Box>
            </HStack>
          </Flex>
        </Container>
      </Box>

      <SignInModal isOpen={isSignInOpen} onClose={onSignInClose} />
      <VenueAgreementModal
        isOpen={isAgreementOpen}
        onClose={onAgreementClose}
        user={user}
        onSigned={() => {
          setAgreementSigned(true);
          onAgreementClose();
          // Payouts are the only thing left between them and the form.
          if (payoutsReady) onVenueModalOpen();
          else onPayoutOpen();
        }}
      />
      <VenuePayoutModal
        isOpen={isPayoutOpen}
        onClose={onPayoutClose}
        user={user}
        returnPath={RETURN_PATH}
        alreadyConnected={stripeConnected}
        onLinked={() => {
          setPayoutsReady(true);
          onPayoutClose();
          onVenueModalOpen();
        }}
      />
      <VenueOnboardingModal isOpen={isVenueModalOpen} onClose={onVenueModalClose} user={user} />
      <HowItWorksModal isOpen={isHowItWorksOpen} onClose={onHowItWorksClose} />
    </Box>
  );
};
